import React, { useState } from 'react';
import { User } from 'lucide-react';

// Professional silhouette placeholder SVG
const SilhouettePlaceholder = ({ initials, size = 80 }) => (
  <div 
    className="relative flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800 text-slate-400"
    style={{ width: size, height: size }}
  >
    {initials ? (
      <span 
        className="font-bold text-slate-300"
        style={{ fontSize: size * 0.35 }}
      >
        {initials}
      </span>
    ) : (
      <User className="text-slate-500" style={{ width: size * 0.5, height: size * 0.5 }} />
    )}
  </div>
);

export const ProfilePhoto = ({ 
  src, 
  alt, 
  initials,
  size = 80,
  className = '',
  federationAccent = false 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleError = () => {
    setImageError(true);
  };

  const handleLoad = () => {
    setImageLoaded(true);
  };

  // Container styles with subtle enhancements
  const containerStyle = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };

  return (
    <div 
      className={`relative rounded-full overflow-hidden ${className}`}
      style={containerStyle}
    >
      {/* Subtle outer glow ring */}
      <div 
        className={`absolute inset-0 rounded-full ${
          federationAccent 
            ? 'ring-2 ring-primary/30 ring-offset-2 ring-offset-background' 
            : 'ring-1 ring-border/50'
        }`}
        style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)' }}
      />
      
      {/* Loading skeleton */}
      {!imageLoaded && !imageError && src && (
        <div 
          className="absolute inset-0 bg-muted/50 animate-pulse rounded-full"
        />
      )}

      {/* Actual image or placeholder */}
      {!imageError && src ? (
        <img
          src={src}
          alt={alt}
          onError={handleError}
          onLoad={handleLoad}
          loading="lazy"
          className={`w-full h-full object-cover rounded-full transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ 
            aspectRatio: '1/1',
          }}
        />
      ) : (
        <div className="w-full h-full rounded-full overflow-hidden">
          <SilhouettePlaceholder initials={initials} size={size} />
        </div>
      )}

      {/* Subtle inner shadow for depth */}
      <div 
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{ 
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1), inset 0 -1px 2px rgba(255,255,255,0.05)' 
        }}
      />
    </div>
  );
};

export default ProfilePhoto;
